import { Center, Flex } from "@mantine/core";
import { useState, useMemo, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import ApexCharts from "apexcharts";
import { PresentionChart, TrendUp } from "iconsax-react";
import { useParams } from "react-router-dom";
import { IParamsPerformance, useProductPerformance } from "../../../hooks";
import { countdownDate } from "../../../utils/date";
import { client_selected } from "../../../client";
import FiltersPerformance from "../../../components/PerformanceChart/FiltersPerformance";
import BulletsPerformance from "../../../components/PerformanceChart/BulletsPerformance";
import AccordionDescriptionsItem from "../../../components/Accordion/AccordionDescriptions/AccordionItem";
import { EDiary, EIsBackTest, EPeriod } from "../../../interfaces/Filters";

export interface IProps {
  initialDate: string;
}

export default function PerformanceChart({ initialDate }: IProps) {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const id = slug?.split("&id=")[1];
  const height = 350;

  const [query, setQuery] = useState<IParamsPerformance>({
    productId: id ? [Number(id)] : [],
    is_backtest: EIsBackTest.All,
    diary: EDiary.Accumulated,
    period: EPeriod.All,
    symbols: undefined,
    close_date_init: new Date(
      new Date().setDate(new Date().getDate() - countdownDate(initialDate))
    ),
    close_date_end: new Date(),
  });
  const [chartKey, setChartKey] = useState(i18n.language);

  const { data: responseDataPerformance, isFetched: isFetchedPerformance } =
    useProductPerformance(query);
  const colors = client_selected.colors;

  const dataPerformance = responseDataPerformance?.data;
  const testToRealTransitionDate = responseDataPerformance?.backtest_date || "";
  const initialDateChart = responseDataPerformance?.data[0]?.date || "";

  const processedData = useMemo(() => {
    if (!dataPerformance || !isFetchedPerformance) return [];

    return dataPerformance.map((item) => {
      let rawValue: number;
      let textValue: string;

      // Verifica o tipo de dados baseado no query.diary
      switch (query.diary) {
        case EDiary.Accumulated:
          rawValue = item.value_acumulated;
          textValue = item.value_acumulated_text;
          break;
        case EDiary.Daily:
          rawValue = item.value;
          textValue = item.value_text;
          break;
        case EDiary.AccumulatedPercenteg:
          rawValue = item.percentage_acumulated;
          textValue = item.percentage_acumulated_text;
          break;
        case EDiary.DailyPercenteg:
          rawValue = item.percentage;
          textValue = item.percentage_text;
          break;
        default:
          // Fallback para o comportamento anterior
          rawValue = item.value_acumulated;
          textValue = item.value_acumulated_text;
      }

      // Converte para número se necessário
      const numericValue =
        typeof rawValue === "number"
          ? rawValue
          : parseFloat(String(rawValue).replace(",", "."));

      return {
        x: item.date,
        y: isNaN(numericValue) ? 0 : numericValue,
        valueText: textValue,
      };
    });
  }, [dataPerformance, isFetchedPerformance, query.diary]);

  const yValues = processedData
    .map((d) => Number(d.y))
    .filter((v) => !isNaN(v));
  const hasValues = yValues.length > 0;
  const minY = hasValues ? Math.min(...yValues) : 0;
  const maxY = hasValues ? Math.max(...yValues) : 0;

  let colorLimit = 50;
  if (hasValues) {
    const range = maxY - minY;
    if (range === 0) {
      colorLimit = maxY > 0 ? 100 : maxY < 0 ? 0 : 50;
    } else {
      colorLimit = ((maxY - 0) / range) * 100;
    }
    if (colorLimit < 0) colorLimit = 0;
    if (colorLimit > 100) colorLimit = 100;
  }

  const categories = processedData.map((d) => d.x);
  const valueTextByIndex = processedData.map((d) => d.valueText);
  const series = [
    {
      name: t("marketplace.detalhe.graphic_performance.performance"),
      data: processedData.map((d) => d.y),
    },
  ];

  const sampleValueText =
    processedData.find((d) => !!d.valueText)?.valueText ?? "";
  const prefixMatch = sampleValueText.match(/^\s*([^0-9\-.,]+)/);
  const suffixMatch = sampleValueText.match(/([^0-9\-.,]+)\s*$/);
  const yPrefix = prefixMatch?.[1]?.trim?.() ?? "";
  const ySuffix = suffixMatch?.[1]?.trim?.() ?? "";

  const options: ApexCharts.ApexOptions = {
    chart: {
      id: "area-datetime",
      height,
      type: "area",
      toolbar: { tools: { download: false }, show: false },
      zoom: {
        enabled: false,
        type: "x",
        autoScaleYaxis: true,
        zoomedArea: { fill: { color: "#3146BB", opacity: 0.1 } },
      },
      offsetX: 0,
      locales: [
        {
          name: "pt-BR",
          options: {
            months: [
              "Janeiro",
              "Fevereiro",
              "Março",
              "Abril",
              "Maio",
              "Junho",
              "Julho",
              "Agosto",
              "Setembro",
              "Outubro",
              "Novembro",
              "Dezembro",
            ],
            shortMonths: [
              "Jan",
              "Fev",
              "Mar",
              "Abr",
              "Mai",
              "Jun",
              "Jul",
              "Ago",
              "Set",
              "Out",
              "Nov",
              "Dez",
            ],
            days: [
              "Domingo",
              "Segunda",
              "Terça",
              "Quarta",
              "Quinta",
              "Sexta",
              "Sábado",
            ],
            shortDays: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
            toolbar: {
              download: "Download",
              selection: "Seleção",
              selectionZoom: "Zoom da Seleção",
              zoomIn: "Aumentar Zoom",
              zoomOut: "Diminuir Zoom",
              pan: "Panorâmica",
              reset: "Resetar Zoom",
            },
          },
        },
        {
          name: "es",
          options: {
            months: [
              "Enero",
              "Febrero",
              "Marzo",
              "Abril",
              "Mayo",
              "Junio",
              "Julio",
              "Agosto",
              "Septiembre",
              "Octubre",
              "Noviembre",
              "Diciembre",
            ],
            shortMonths: [
              "Ene",
              "Feb",
              "Mar",
              "Abr",
              "May",
              "Jun",
              "Jul",
              "Ago",
              "Sep",
              "Oct",
              "Nov",
              "Dic",
            ],
            days: [
              "Domingo",
              "Lunes",
              "Martes",
              "Miércoles",
              "Jueves",
              "Viernes",
              "Sábado",
            ],
            shortDays: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"],
            toolbar: {
              download: "Descargar",
              selection: "Selección",
              selectionZoom: "Zoom de Selección",
              zoomIn: "Acercar",
              zoomOut: "Alejar",
              pan: "Panorámica",
              reset: "Restablecer Zoom",
            },
          },
        },
        {
          name: "en-US",
          options: {
            months: [
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ],
            shortMonths: [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ],
            days: [
              "Sunday",
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
            ],
            shortDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
            toolbar: {
              download: "Download",
              selection: "Selection",
              selectionZoom: "Selection Zoom",
              zoomIn: "Zoom In",
              zoomOut: "Zoom Out",
              pan: "Pan",
              reset: "Reset Zoom",
            },
          },
        },
      ],
      defaultLocale:
        i18n.language === "pt-BR"
          ? "pt-BR"
          : i18n.language === "es"
          ? "es"
          : "en-US",
    },
    dataLabels: { enabled: false },
    xaxis: {
      type: "datetime",
      categories,
      tickPlacement: "on",
      tooltip: { enabled: false },
      labels: {
        datetimeUTC: true,
        datetimeFormatter: {
          year: "yyyy",
          month: "MMM 'yy",
          day: "dd MMM",
          hour: "HH:mm",
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => {
          const hasPrefix = yPrefix.length > 0;
          const hasSuffix = ySuffix.length > 0;
          return `${hasPrefix ? `${yPrefix} ` : ""}${value}${
            hasSuffix ? ` ${ySuffix}` : ""
          }`;
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        type: "vertical",
        opacityTo: 1,
        opacityFrom: 1,
        colorStops: [
          {
            offset: 0,
            color: "#146DF2A6",
            opacity: 0.8,
          },
          {
            offset: colorLimit,
            color: "#146DF2A6",
            opacity: 0.1,
          },
          {
            offset: colorLimit,
            color: "#F43333",
            opacity: 0.1,
          },
          {
            offset: 100,
            color: "#F43333",
            opacity: 0.8,
          },
        ],
      },
    },
    annotations: {
      xaxis: [
        {
          x: new Date(testToRealTransitionDate).getTime(),
          borderColor: "#999",
          label: {
            text: t("marketplace.detalhe.graphic_performance.backtesting"),
            style: { color: "#fff", background: "#775DD0" },
          },
        },
        {
          x: new Date(initialDateChart).getTime(),
          borderColor: "#999",
          label: {
            text: t("marketplace.detalhe.graphic_performance.initial_date"),
            style: { color: "#fff", background: "#006000" },
          },
        },
      ],
      yaxis: [
        {
          y: 0,
          borderColor: "#999",
        },
      ],
    },
    stroke: {
      curve: "straight",
      width: 1,
      colors: ["#5E71D7"],
    },
    markers: {
      size: 4,
      colors: ["#5E71D7"],
    },
    tooltip: {
      marker: {
        show: false,
      },
      y: {
        title: {
          formatter: (value, opts) => {
            return `${t("marketplace.detalhe.graphic_performance.title")}:`;
          },
        },
        formatter: (value, opts) => {
          const dataPointIndex = opts?.dataPointIndex ?? 0;
          const vt = valueTextByIndex[dataPointIndex];
          if (typeof vt === "string" && vt.trim().length > 0) return vt;
          const hasPrefix = yPrefix.length > 0;
          const hasSuffix = ySuffix.length > 0;
          return `${hasPrefix ? `${yPrefix} ` : ""}${value}${
            hasSuffix ? ` ${ySuffix}` : ""
          }`;
        },
      },
    },
    grid: {
      borderColor: "#f1f1f1",
    },
  };

  // Recarrega o gráfico quando o idioma muda
  useEffect(() => {
    setChartKey(i18n.language);
  }, [i18n.language]);

  return (
    <>
      <AccordionDescriptionsItem
        value="GraphicPerformance"
        Icon={PresentionChart}
        Panel={
          <Flex
            justify="end"
            bg="basic.0"
            pos={"relative"}
            sx={{ borderRadius: "10px" }}
            direction={"column"}
          >
            <FiltersPerformance
              query={query}
              setQuery={setQuery}
              initialDate={initialDate}
              available_symbols={responseDataPerformance?.available_symbols}
            />
            {isFetchedPerformance ? (
              !!responseDataPerformance ? (
                processedData.length >= 1 ? (
                  <ReactApexChart
                    key={chartKey}
                    options={options}
                    series={series}
                    type="area"
                    height={height}
                  />
                ) : (
                  <Center h={height}>{t("global.pagination.no_data")}</Center>
                )
              ) : (
                <Center h={height}>{t("global.pagination.no_data")}</Center>
              )
            ) : (
              <Center h={height}>{t("global.button.loading")}</Center>
            )}
          </Flex>
        }
        title={t("marketplace.detalhe.graphic_performance.title")}
      />
      <AccordionDescriptionsItem
        value="GeneralStatistics"
        Icon={TrendUp}
        title={t("marketplace.detalhe.general_statistics.title")}
        Panel={
          <Flex
            justify="end"
            bg="basic.0"
            pos={"relative"}
            sx={{ borderRadius: "10px" }}
            direction={"column"}
          >
            <FiltersPerformance
              query={query}
              setQuery={setQuery}
              initialDate={initialDate}
              available_symbols={responseDataPerformance?.available_symbols}
            />
            {isFetchedPerformance ? (
              !!responseDataPerformance ? (
                <BulletsPerformance dataPerformance={responseDataPerformance} />
              ) : (
                <Center h={height}>{t("global.pagination.no_data")}</Center>
              )
            ) : (
              <Center h={height}>{t("global.button.loading")}</Center>
            )}
          </Flex>
        }
      />
    </>
  );
}
